import React, { useState } from "react";
import { Share2, Link as LinkIcon, Mail, MessageCircle, Facebook, Twitter } from "lucide-react";
import { toast } from "sonner";

// Reusable share bar for individual recipes.
// Encodes URL + share text and opens each network in a new window.
export function ShareBar({ url, title, image = "", compact = false, label = "Share this recipe" }) {
  const [open, setOpen] = useState(false);
  const encodedUrl = encodeURIComponent(url);
  const encodedTitle = encodeURIComponent(title);
  const encodedImage = encodeURIComponent(image);
  const encodedText = encodeURIComponent(`${title} — an exclusive recipe by Chef Jacinto Samuel.`);

  const networks = [
    {
      key: "whatsapp",
      label: "WhatsApp",
      Icon: MessageCircle,
      href: `https://api.whatsapp.com/send?text=${encodedText}%20${encodedUrl}`,
    },
    {
      key: "facebook",
      label: "Facebook",
      Icon: Facebook,
      href: `https://www.facebook.com/sharer/sharer.php?u=${encodedUrl}`,
    },
    {
      key: "twitter",
      label: "X (Twitter)",
      Icon: Twitter,
      href: `https://twitter.com/intent/tweet?url=${encodedUrl}&text=${encodedText}`,
    },
    {
      key: "pinterest",
      label: "Pinterest",
      IconChar: "P",
      href: `https://pinterest.com/pin/create/button/?url=${encodedUrl}&media=${encodedImage}&description=${encodedText}`,
    },
    {
      key: "email",
      label: "Email",
      Icon: Mail,
      href: `mailto:?subject=${encodedTitle}&body=${encodedText}%20${encodedUrl}`,
    },
  ];

  const copyLink = async () => {
    try {
      await navigator.clipboard.writeText(url);
      toast.success("Link copied to clipboard");
    } catch {
      toast.error("Could not copy the link");
    }
  };

  const nativeShare = async () => {
    if (navigator.share) {
      try {
        await navigator.share({ title, text: title, url });
      } catch {
        // user cancelled
      }
    } else {
      setOpen((v) => !v);
    }
  };

  return (
    <div className="mt-6" data-testid="share-bar">
      {!compact && <div className="overline text-zinc-500 mb-3">{label}</div>}
      <div className="flex items-center gap-2 flex-wrap">
        {networks.map(({ key, label: l, Icon, IconChar, href }) => (
          <a
            key={key}
            href={href}
            target="_blank"
            rel="noopener noreferrer"
            aria-label={l}
            title={l}
            data-testid={`share-${key}`}
            className="w-10 h-10 rounded-full border border-white/10 hover:border-[#D4AF37]/60 text-zinc-400 hover:text-[#D4AF37] flex items-center justify-center transition-all duration-300 hover:-translate-y-0.5"
          >
            {Icon ? <Icon className="w-4 h-4" /> : <span className="text-sm font-serif">{IconChar}</span>}
          </a>
        ))}
        <button
          onClick={copyLink}
          aria-label="Copy link"
          title="Copy link"
          data-testid="share-copy"
          className="w-10 h-10 rounded-full border border-white/10 hover:border-[#D4AF37]/60 text-zinc-400 hover:text-[#D4AF37] flex items-center justify-center transition-all duration-300 hover:-translate-y-0.5"
        >
          <LinkIcon className="w-4 h-4" />
        </button>
        {typeof navigator !== "undefined" && navigator.share && (
          <button
            onClick={nativeShare}
            aria-label="Share"
            title="Share"
            data-testid="share-native"
            className="w-10 h-10 rounded-full border border-white/10 hover:border-[#D4AF37]/60 text-zinc-400 hover:text-[#D4AF37] flex items-center justify-center transition-all duration-300 hover:-translate-y-0.5"
          >
            <Share2 className="w-4 h-4" />
          </button>
        )}
      </div>
    </div>
  );
}

export default ShareBar;
